import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { octahedronSolid, orientedFace, planPoint, pyramidOverRing, ringPoints } from '../../solids';
import { pointedArchRise, pointedWindow } from '../../panels';
import { rectWall } from '../masonry';

/**
 * An octagonal slate needle crowned by an open lantern clad in lead under a bulbous cap whose ribs
 * carry lead crockets, as Janse describes the Oude Kerk spire of c.1510 (Janse 2004, pp. 134-135) and
 * the 1544 woodcut draws it: the lantern a little wider than the needle it stands on.
 */
export const NEEDLE = {
  segments: 8,
  /** The needle's top as a share of the lantern's radius: the lantern overhangs it. */
  neck: 0.85,
  opening: { widthShare: 0.5, sill: 0.4, topDrop: 0.5 },
  /** The cap's shoulder: its height as a share of the cap and its radius as a share of the lantern's. */
  cap: { shoulder: 0.4, radius: 0.8 },
  crocket: { every: 0.55, size: 0.14, reach: 0.08 },
  finialRise: 0.15,
  finial: 0.35,
} as const;

export interface NeedleSpec {
  readonly center: Vec2;
  /** Circumradius of the octagon at the foot. */
  readonly radius: number;
  readonly y0: number;
  readonly lanternFoot: number;
  readonly lanternTop: number;
  readonly tipY: number;
  /** Circumradius of the lantern. */
  readonly lanternRadius: number;
  readonly crockets?: boolean;
}

export interface NeedleStyles {
  readonly slate: VertexStyle;
  readonly lead: VertexStyle;
  readonly opening: VertexStyle;
  readonly stone: VertexStyle;
}

/* Flat faces toward the axes, over the sides of a square tower. */
const PHASE = Math.PI / NEEDLE.segments;

function outwardAt(i: number, rise: number): Vec3 {
  const a = PHASE + ((i + 0.5) * 2 * Math.PI) / NEEDLE.segments;
  return [Math.cos(a), rise, -Math.sin(a)];
}

/** The faces between two octagonal rings, the lower at y0 and the upper at y1. */
function band(builder: MeshBuilder, style: VertexStyle, frame: Frame, lower: Ring, upper: Ring, ys: readonly [number, number], rise: number): void {
  lower.forEach((p, i) => {
    const j = (i + 1) % lower.length;
    const quad = [planPoint(p[0], ys[0], p[1]), planPoint(lower[j]![0], ys[0], lower[j]![1]), planPoint(upper[j]![0], ys[1], upper[j]![1]), planPoint(upper[i]![0], ys[1], upper[i]![1])];
    orientedFace(builder, style, frame, quad, outwardAt(i, rise));
  });
}

function lantern(builder: MeshBuilder, styles: NeedleStyles, frame: Frame, ring: Ring, spec: NeedleSpec): void {
  const face = Math.hypot(ring[1]![0] - ring[0]![0], ring[1]![1] - ring[0]![1]);
  const o = NEEDLE.opening;
  const width = o.widthShare * face;
  const top = spec.lanternTop - spec.lanternFoot - o.topDrop;
  const opening = [pointedWindow(face / 2, o.sill, width, top - pointedArchRise(width), 3)];
  const masonry = { wall: styles.lead, roof: styles.slate, opening: styles.opening };
  ring.forEach((from, i) => { rectWall(builder, masonry, frame, [from, ring[(i + 1) % ring.length]!], [spec.lanternFoot, spec.lanternTop], opening); });
}

/** Lead crockets climbing each rib of the cap from its foot through its shoulder toward the tip. */
function crockets(builder: MeshBuilder, style: VertexStyle, frame: Frame, rib: readonly Vec3[]): void {
  const c = NEEDLE.crocket;
  const [foot, shoulder, tip] = rib as [Vec3, Vec3, Vec3];
  const lengths = [Math.hypot(shoulder[0] - foot[0], shoulder[1] - foot[1], shoulder[2] - foot[2]), Math.hypot(tip[0] - shoulder[0], tip[1] - shoulder[1], tip[2] - shoulder[2])] as const;
  const total = lengths[0] + lengths[1];
  const count = Math.floor(total / c.every) - 1;
  for (let k = 1; k <= count; k++) {
    const s = k * c.every;
    const [a, b, t] = s <= lengths[0] ? [foot, shoulder, s / lengths[0]] : [shoulder, tip, (s - lengths[0]) / lengths[1]];
    const p: Vec3 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
    const out = Math.hypot(p[0] - tip[0], p[2] - tip[2]);
    const scale = out > 0 ? c.reach / out : 0;
    octahedronSolid(builder, style, frame, [p[0] + (p[0] - tip[0]) * scale, p[1], p[2] + (p[2] - tip[2]) * scale], c.size);
  }
}

/** The bulbous cap: out to a shoulder above the straight line from the lantern's top to the tip, then in to the tip. */
function cap(builder: MeshBuilder, styles: NeedleStyles, frame: Frame, top: Ring, spec: NeedleSpec): void {
  const shoulderY = spec.lanternTop + NEEDLE.cap.shoulder * (spec.tipY - spec.lanternTop);
  const shoulder = ringPoints(spec.center, spec.lanternRadius * NEEDLE.cap.radius, NEEDLE.segments, PHASE);
  band(builder, styles.lead, frame, top, shoulder, [spec.lanternTop, shoulderY], 0.6);
  pyramidOverRing(builder, { roof: styles.lead, wall: styles.lead }, frame, shoulder, shoulderY, { plan: spec.center, y: spec.tipY });
  if (spec.crockets === false) return;
  const tip = planPoint(spec.center[0], spec.tipY, spec.center[1]);
  top.forEach((p, i) => {
    const s = shoulder[i]!;
    crockets(builder, styles.lead, frame, [planPoint(p[0], spec.lanternTop, p[1]), planPoint(s[0], shoulderY, s[1]), tip]);
  });
}

export function needleSpire(builder: MeshBuilder, styles: NeedleStyles, frame: Frame, spec: NeedleSpec): void {
  const foot = ringPoints(spec.center, spec.radius, NEEDLE.segments, PHASE);
  const neck = ringPoints(spec.center, spec.lanternRadius * NEEDLE.neck, NEEDLE.segments, PHASE);
  const lanternRing = ringPoints(spec.center, spec.lanternRadius, NEEDLE.segments, PHASE);
  band(builder, styles.slate, frame, foot, neck, [spec.y0, spec.lanternFoot], 0.5);
  band(builder, styles.lead, frame, neck, lanternRing, [spec.lanternFoot, spec.lanternFoot], -1);
  lantern(builder, styles, frame, lanternRing, spec);
  cap(builder, styles, frame, lanternRing, spec);
  const ball = planPoint(spec.center[0], spec.tipY + NEEDLE.finialRise, spec.center[1]);
  octahedronSolid(builder, styles.stone, frame, ball, NEEDLE.finial);
}
